import React, { useState, useEffect, useRef } from "react";
import { Input, InputRef, Modal } from "antd";
import { toast } from "react-toastify";
import CustomLoader from "../../../common/UI/Loader";
import { LeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import apiInstance from "../../../../services/apiService";
import { storageService } from "../../../../services/storageService";
import ENDPOINTS from "../../../../services/endpoint";
import GradientButton from "../../../common/UI/GradientButton";
import "./index.scss";

interface DeleteAccProps {
  handleBackToPrevious: () => void;
}

const DeleteAccount: React.FC<DeleteAccProps> = ({ handleBackToPrevious }) => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isConfirmationModalOpen, setConfirmationModalOpen] =
    useState<boolean>(false);
  const [isFetching, setIsFetching] = useState(true);
  const [apiResponse, setApiResponse] = useState<string | null>(null);
  const otpRefs = useRef<(InputRef | null)[]>([]);

  const fetchDeleteConfirmation = async () => {
    try {
      const response = await apiInstance.delete(ENDPOINTS.DELETE_ACCOUNT);
      setApiResponse(response.data.message);
      setTimer(60);
    } catch (error: any) {
      toast.error(error.message || "Failed to send confirmation code.");
    } finally {
      setIsFetching(false);
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return; // Allow only numeric input

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < otp.length - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyDelete = async () => {
    setLoading(true);
    try {
      const response = await apiInstance.post(ENDPOINTS.CONFIRM_DELETE, {
        accessCode: otp.join(""),
      });

      if (response.status === 200) {
        toast.success("Your account has been successfully deleted.");
        storageService.clearAll();
        navigate("/login");
      }
    } catch (error: any) {
      toast.error(error.message || "Invalid OTP or server error.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmationModal = () => {
    const accessCode = otp.join("");

    if (accessCode.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP.");
      return;
    }

    setConfirmationModalOpen(true);
  };

  useEffect(() => {
    fetchDeleteConfirmation();
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  return (
    <>
      <div className="delete-account-sider">
        {isFetching ? (
          <div className="loader-wrapper">
            <CustomLoader />
          </div>
        ) : (
          <div className="delete-container">
            <LeftOutlined
              style={{ color: "#6563EC", alignSelf: "self-start" }}
              onClick={handleBackToPrevious}
            />

            <img
              src="/assets/images/account-deletion.png"
              alt="Delete"
              className="delete-img"
            />

            <h2 className="delete-title">Verify Account Deletion</h2>
            <p className="delete-text">{apiResponse}</p>

            <div className="otp-container">
              {otp.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => {
                    otpRefs.current[index] = el;
                  }}
                  maxLength={1}
                  className="otp-input"
                  value={digit}
                  onChange={(e) => handleOtpChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              ))}
            </div>

            <p className="delete-timer">
              {timer > 0 ? (
                <>
                  You can send OTP after:{" "}
                  <span className="timer">{`00:${
                    timer < 10 ? `0${timer}` : timer
                  }`}</span>
                </>
              ) : (
                <a onClick={fetchDeleteConfirmation}>Resend OTP</a>
              )}
            </p>

            <GradientButton
              className="delete-btn"
              onClick={handleConfirmationModal}
              loading={loading}
            >
              Verify & Delete
            </GradientButton>
          </div>
        )}
      </div>

      <Modal
        title="Confirm Deletion"
        className="custom-modal"
        centered
        open={isConfirmationModalOpen}
        onOk={handleVerifyDelete}
        onCancel={() => setConfirmationModalOpen(false)}
        okText="Yes"
        cancelText="No"
        okButtonProps={{
          className: "modal-ok-btn",
          loading: loading,
        }}
        cancelButtonProps={{
          className: "modal-cancel-btn",
          disabled: loading,
          type: "text",
        }}
      >
        <p>Are you sure you want to delete your account?</p>
      </Modal>
    </>
  );
};

export default DeleteAccount;
